import type { FC } from 'react';
import { useContext } from 'react';
import classNames from 'classnames';

import ConfigContext from 'app/common/ConfigContext';
import {
  getEBattleSubdivisionLabels,
  getStaticMapImage
} from 'app/config/videogameSpecific.config.helper';
import { withBackgroundImage } from 'app/utils/style';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';
import DisplayContext from 'app/common/DisplayContext';
import GameTimer from 'app/components/GameTimer';
import ESoccerScoreboard from 'app/components/Blocks/NameBlock/ESoccerScoreboard';
import EBattleColumnarScoreboard from 'app/components/Blocks/NameBlock/EBattleColumnarScoreboard';
import { getCurrentSubdivision } from 'app/components/Blocks/NameBlock/EBattleColumnarScoreboard/utils';
import ETennisScoreboard from 'app/components/Blocks/NameBlock/ETennisScoreboard';
import { getScoreboardGame } from 'app/components/Blocks/NameBlock/MatchScore/utils';

import style from './style.module.scss';

interface Props {
  data: NameBlockMatch;
}

const EBattleNameBlock: FC<Props> = ({ data }) => {
  const { singleBlockDisplay, displayedBlocks } = useContext(DisplayContext);
  const { lang } = useContext(ConfigContext);

  const scoreboardGame = getScoreboardGame(data);
  const isPreMatch =
    data.match_status === 'not_started' &&
    (!scoreboardGame || scoreboardGame.status === 'not_started');
  const slug = data.videogame_slug;
  const hasSideMap = slug === 'e-tennis' || slug === 'e-basketball' || slug === 'e-hockey';
  const staticMapUrl =
    isPreMatch || hasSideMap || slug === 'e-soccer' ? getStaticMapImage(slug) : null;
  const gameMap = staticMapUrl ? { id: 0, image_url: staticMapUrl, name: '' } : undefined;
  const showMap = gameMap && (isPreMatch || hasSideMap || slug === 'e-soccer');

  return (
    <BlockTemplate
      className={classNames('override-block_name', {
        [style.withMap]: showMap,
        [style.withSideMap]: showMap && !isPreMatch && hasSideMap,
        [style.flatBottomMap]: showMap && (isPreMatch || !hasSideMap) && !singleBlockDisplay,
        [style.fullHeightDisplay]:
          displayedBlocks.length === 1 && displayedBlocks[0] === 'name' && !singleBlockDisplay
      })}
      containerClassname={style.content}
    >
      {slug === 'e-soccer' && (
        <ESoccerScoreboard
          game={scoreboardGame as GameESoccer | undefined}
          opponent1={data.opponents.at(0)}
          opponent2={data.opponents.at(1)}
        />
      )}
      {(slug === 'e-basketball' || slug === 'e-hockey') && (
        <EBattleColumnarScoreboard
          game={scoreboardGame}
          opponent1={data.opponents.at(0)}
          opponent2={data.opponents.at(1)}
          slug={slug}
          labels={getEBattleSubdivisionLabels(slug, lang)}
        />
      )}
      {slug === 'e-tennis' && (
        <ETennisScoreboard
          games={data.games as GameETennis[]}
          opponent1={data.opponents.at(0)}
          opponent2={data.opponents.at(1)}
          labels={getEBattleSubdivisionLabels('e-tennis', lang)}
        />
      )}

      {showMap && (
        <div
          style={withBackgroundImage(gameMap.image_url)}
          className={classNames(style.map, {
            [style.mapPreMatch]: isPreMatch || !hasSideMap,
            [style.mapSide]: !isPreMatch && hasSideMap,
            [style.mapWithBorderRadius]: singleBlockDisplay
          })}
        >
          {isPreMatch && data.begin_at && (
            <div className={style.dateBadge}>
              {new Date(data.begin_at).toLocaleDateString(undefined, {
                month: '2-digit',
                day: '2-digit'
              })}
              {' • '}
              {new Date(data.begin_at).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              })}
            </div>
          )}
          {scoreboardGame?.status === 'running' &&
            (slug === 'e-basketball' || slug === 'e-hockey') &&
            (() => {
              const currentSub = getCurrentSubdivision(scoreboardGame, slug);
              const labels = getEBattleSubdivisionLabels(slug, lang);
              const label = currentSub !== null ? labels[currentSub - 1] : null;
              return (
                label && (
                  <div className={style.mapOverlay}>
                    <span className={style.mapPeriodLabel}>{label}</span>
                    {scoreboardGame?.timer && (
                      <GameTimer
                        timer={scoreboardGame.timer}
                        videogame={slug}
                        live
                        className={style.mapTimer}
                      />
                    )}
                  </div>
                )
              );
            })()}
        </div>
      )}
    </BlockTemplate>
  );
};

EBattleNameBlock.displayName = 'EBattleNameBlock';

export default EBattleNameBlock;
